<script>
import detectFocusChange from "@/mixins/detectFocusChange";
import gameUrls from "@/mixins/gameUrls";
import { database, firestore } from "@/services/firebase";
import MatchHelper from "@/utils/match-helper";
import * as PlayerHelper from "@shared/utils/player-helper";
import firebase from "firebase/app";
import { mapActions, mapGetters } from "vuex";
import {
  CHALLENGE_STATUS,
  CHALLENGE_TYPE,
  MATCH_DEFAULT,
  ONLINE_STATUS,
} from "../../constants";
import ChallengeReceivedModal from "./ChallengeReceivedModal";
import ChallengeSentModal from "./ChallengeSentModal";

export default {
  name: "roomRenderless",

  components: {
    ChallengeReceivedModal,
    ChallengeSentModal,
  },

  props: ["roomId"],

  mixins: [detectFocusChange, gameUrls],

  data() {
    return {
      players: [],
      challengedPlayer: {},
      hasSeenChallenge: false, // Track if we've seen a challenge to avoid race condition
    };
  },

  computed: {
    ...mapGetters(["getUserId", "getPlayer", "getStats", "getOnlineStatus"]),

    userId() {
      return this.getUserId;
    },

    otherPlayers() {
      return this.players.filter(
        (player) => player.user_id && player.user_id !== this.userId
      );
    },

    myPlayer() {
      return this.players.find(
        (player) => player.user_id && player.user_id === this.userId
      );
    },

    receivedChallenge() {
      if (!this.myPlayer || !this.myPlayer.user_id) return null;
      return this.myPlayer.challenge;
    },
  },

  watch: {
    userId: {
      immediate: true,
      handler(userId) {
        if (!userId) return;
        this.joinRoom();
      },
    },

    getStats: {
      immediate: true,
      handler() {
        if (!this.userId) return;
        if (!this.getStats) return;
        if (
          this.myPlayer &&
          this.myPlayer.stats &&
          this.myPlayer.stats.rating === this.getStats.rating
        ) {
          return;
        }
        this.joinRoom();
      },
    },

    challengedPlayer: function (challengedPlayer) {
      console.log("watch challengedPlayer", challengedPlayer);
      if (!challengedPlayer) return;

      // Handle challenge removal - only unbind if we've seen a challenge before
      if (!challengedPlayer.challenge) {
        if (this.hasSeenChallenge) {
          // Challenge was removed (accepted/denied/canceled)
          this.$rtdbUnbind("challengedPlayer");
          this.hasSeenChallenge = false;
        }
        // If we haven't seen a challenge yet, don't unbind - we're waiting for the update to propagate
        return;
      }

      // Verify this challenge is from us
      if (challengedPlayer.challenge.player.user_id !== this.userId) {
        this.$rtdbUnbind("challengedPlayer");
        this.hasSeenChallenge = false;
        return;
      }

      // Mark that we've seen a valid challenge
      this.hasSeenChallenge = true;

      // If challenge has been accepted and matchId is set, navigate to the game
      if (challengedPlayer.challenge.matchId) {
        const matchId = challengedPlayer.challenge.matchId;
        database
          .ref(
            `rooms/${this.roomId}/players/${this.challengedPlayer.user_id}/challenge`
          )
          .remove();
        this.$rtdbUnbind("challengedPlayer");
        this.hasSeenChallenge = false;
        // Go to Match
        this.$router.push({
          path: this.getGameUrl(matchId),
        });
      }
    },

    async tabFocus(value) {
      this.setIsAway(!value);
    },
  },

  methods: {
    ...mapActions(["setIsAway"]),

    promptDuration(player) {
      this.$buefy.dialog.prompt({
        message: "Minutes per side?",
        inputAttrs: {
          type: "number",
          value: 5,
          maxlength: 2,
          min: 5,
          max: 15,
          step: 10,
        },
        cancelText: "Cancel",
        confirmText: "Challenge",
        type: "is-info",
        trapFocus: true,
        onConfirm: (value) => {
          this.sendChallenge(player, value * 60);
        },
      });
    },

    isOnline(player) {
      if (!player || !player.user_id) return false;
      if (!this.players || !this.players.length) return false;
      return !!this.players.find((p) => p.user_id === player.user_id);
    },

    sendChallenge(
      player,
      duration = MATCH_DEFAULT.DURATION,
      variant = MATCH_DEFAULT.VARIANT
    ) {
      if (!player || !player.user_id || player.challenge) return;
      if (!this.isOnline(player)) return;
      console.log("sendChallenge", player.user_id);
      const roomPlayerRef = database.ref(
        `rooms/${this.roomId}/players/${player.user_id}`
      );

      // Reset the flag before sending a new challenge
      this.hasSeenChallenge = false;

      // Bind FIRST to ensure we're listening before the update happens
      this.$rtdbBind("challengedPlayer", roomPlayerRef);

      roomPlayerRef.update({
        challenge: {
          type: CHALLENGE_TYPE.GAME,
          status: CHALLENGE_STATUS.SENT,
          player: {
            ...this.getPlayer,
            timeOffset: parseFloat(
              sessionStorage.getItem("firestoreTimeOffset")
            ),
          },
          duration: duration,
          variant: variant,
          createdAt: firebase.database.ServerValue.TIMESTAMP,
        },
      });

      this.$buefy.toast.open(
        `You have invited ${player.first_name} to a ${
          duration / 60
        }-minute game`
      );
    },

    cancelChallenge() {
      if (!this.challengedPlayer) return;
      console.log("cancelChallenge");
      this.$buefy.toast.open(
        `You have canceled your game invitation to ${this.challengedPlayer.first_name}`
      );

      database
        .ref(
          `rooms/${this.roomId}/players/${this.challengedPlayer.user_id}/challenge`
        )
        .remove();
    },

    denyChallenge() {
      console.log("denyChallenge");
      this.$buefy.toast.open(
        `You have rejected a game invitation from ${this.receivedChallenge.player.first_name}`
      );

      database
        .ref(`rooms/${this.roomId}/players/${this.userId}/challenge`)
        .remove();
    },

    async acceptChallenge() {
      console.log("acceptChallenge");
      if (!this.getPlayer) return;
      if (!this.receivedChallenge || !this.receivedChallenge.player) return;

      if (this.receivedChallenge.type === CHALLENGE_TYPE.SIMUL) {
        const simulId = this.receivedChallenge.simulId;

        // join the simul
        await firestore
          .collection("simuls")
          .doc(simulId)
          .update({
            players: firebase.firestore.FieldValue.arrayUnion(
              this.currentPlayer
            ),
          });

        //Update challenge request
        database
          .ref(`rooms/${this.roomId}/players/${this.userId}/challenge`)
          .update({ simulId: simulId, status: CHALLENGE_STATUS.ACCEPTED });

        if (this.$route.name === "simul") {
          return;
        }

        this.$router.push({
          name: "simul",
          params: {
            simulId: simulId,
          },
        });
      } else {
        let whitePlayer = {
          ...this.getPlayer,
          timeOffset: parseFloat(sessionStorage.getItem("firestoreTimeOffset")),
        };
        let blackPlayer = this.receivedChallenge.player;
        if (Math.random() < 0.5) {
          const temp = whitePlayer;
          whitePlayer = blackPlayer;
          blackPlayer = temp;
        }

        const matchId = await MatchHelper.createMatch({
          whitePlayer: whitePlayer,
          blackPlayer: blackPlayer,
          duration: this.receivedChallenge.duration,
          increment: this.receivedChallenge.increment,
          variant: this.receivedChallenge.variant,
        });
        if (!matchId) return;

        //Update challenge request
        database
          .ref(`rooms/${this.roomId}/players/${this.userId}/challenge`)
          .update({ matchId: matchId, status: CHALLENGE_STATUS.ACCEPTED });

        console.log("goto match", matchId);
        // Go to Match
        this.$router.push({
          path: this.getGameUrl(matchId),
        });
      }
    },

    joinRoom() {
      if (!this.roomId) return;
      if (!this.userId) return;
      if (!this.getPlayer) return;
      const status = this.getOnlineStatus
        ? this.getOnlineStatus
        : ONLINE_STATUS.ACTIVE;
      console.log("joinRoom", this.roomId, this.userId, status);
      const profile = {
        ...this.getPlayer,
        status: status,
        updatedAt: firebase.database.ServerValue.TIMESTAMP,
      };
      if (!this.myPlayer || !this.myPlayer.joinedAt) {
        profile.joinedAt = firebase.database.ServerValue.TIMESTAMP;
      }

      const roomPlayerRef = database.ref(
        `rooms/${this.roomId}/players/${this.userId}`
      );

      // Remove any previous listener before registering a new one to prevent
      // accumulation — each joinRoom() call would otherwise add a permanent
      // .info/connected listener that re-adds the player on every reconnect.
      if (this.connectedRef && this.connectedCallback) {
        this.connectedRef.off("value", this.connectedCallback);
      }

      this.connectedRef = database.ref(".info/connected");
      this.connectedCallback = (snap) => {
        if (snap.val() === true) {
          // Add/Update my profile to the room
          roomPlayerRef.update(profile);

          // When I disconnect, remove my entry in the room
          roomPlayerRef.onDisconnect().remove();
        }
      };
      this.connectedRef.on("value", this.connectedCallback);
    },

    async leaveRoom() {
      if (!this.roomId) return;
      if (!this.userId) return;
      console.log("leaveRoom", this.roomId, this.userId);

      // Stop the .info/connected listener so a reconnect after explicit leave
      // cannot re-add this player to the room.
      if (this.connectedRef && this.connectedCallback) {
        this.connectedRef.off("value", this.connectedCallback);
        this.connectedRef = null;
        this.connectedCallback = null;
      }

      const roomPlayerRef = database.ref(
        `rooms/${this.roomId}/players/${this.userId}`
      );
      await roomPlayerRef.onDisconnect().cancel();
      await roomPlayerRef.remove();
    },

    playerName(player) {
      return PlayerHelper.playerName(player);
    },
  },

  created() {
    this.connectedRef = null;
    this.connectedCallback = null;
    this.$rtdbBind("players", database.ref(`rooms/${this.roomId}/players`));
  },

  async beforeDestroy() {
    await this.leaveRoom();
  },
};
</script>
